import { Loader2 } from 'lucide-react';
import { Text } from '@/components/catalyst/text';
import { cn } from '@/lib/utils';

interface LoadingStateProps {
  /**
   * Type of loading state to display
   */
  type?: 'page' | 'inline' | 'card' | 'table' | 'spinner' | 'dots';
  /**
   * Optional message to display with the loader
   */
  message?: string;
  /**
   * Number of skeleton items to show (for table/card types)
   */
  count?: number;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Size variant for spinner type
   */
  size?: 'sm' | 'md' | 'lg';
}

export function LoadingState({
  type = 'spinner',
  message,
  count = 3,
  className,
  size = 'md',
}: LoadingStateProps) {
  // Size classes for spinner
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  // Page-level loading state
  if (type === 'page') {
    return (
      <div className={cn('flex min-h-[400px] flex-col items-center justify-center p-8', className)}>
        <Loader2
          className={cn('mb-4 animate-spin text-zinc-400 dark:text-zinc-500', sizeClasses.lg)}
        />
        {message && <Text className="text-center text-zinc-600 dark:text-zinc-400">{message}</Text>}
      </div>
    );
  }

  // Inline loading state
  if (type === 'inline') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Loader2
          className={cn('animate-spin text-zinc-400 dark:text-zinc-500', sizeClasses[size])}
        />
        {message && <Text className="text-zinc-600 dark:text-zinc-400">{message}</Text>}
      </div>
    );
  }

  // Card skeleton loading
  if (type === 'card') {
    return (
      <div className={cn('space-y-4', className)}>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-zinc-200 bg-white p-6 dark:border-zinc-700 dark:bg-zinc-800"
          >
            <div className="animate-pulse">
              <div className="mb-4 flex items-center justify-between">
                <div className="h-5 w-1/3 rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="h-5 w-20 rounded bg-zinc-200 dark:bg-zinc-700" />
              </div>
              <div className="space-y-3">
                <div className="h-4 w-full rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="h-4 w-5/6 rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="h-4 w-4/6 rounded bg-zinc-200 dark:bg-zinc-700" />
              </div>
              <div className="mt-6 flex items-center gap-4">
                <div className="h-8 w-24 rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="h-8 w-24 rounded bg-zinc-200 dark:bg-zinc-700" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Table skeleton loading
  if (type === 'table') {
    return (
      <div className={cn('overflow-hidden', className)}>
        <div className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
          {/* Table header skeleton */}
          <div className="border-b border-zinc-200 px-6 py-3 dark:border-zinc-700">
            <div className="flex animate-pulse items-center gap-4">
              <div className="h-4 w-24 rounded bg-zinc-200 dark:bg-zinc-700" />
              <div className="h-4 w-32 rounded bg-zinc-200 dark:bg-zinc-700" />
              <div className="h-4 w-28 rounded bg-zinc-200 dark:bg-zinc-700" />
              <div className="ml-auto h-4 w-20 rounded bg-zinc-200 dark:bg-zinc-700" />
            </div>
          </div>
          {/* Table rows skeleton */}
          {Array.from({ length: count }).map((_, i) => (
            <div
              key={i}
              className="border-b border-zinc-200 px-6 py-4 last:border-b-0 dark:border-zinc-700"
            >
              <div className="flex animate-pulse items-center gap-4">
                <div className="h-4 w-28 rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="h-4 w-40 rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="h-4 w-24 rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="ml-auto h-4 w-16 rounded bg-zinc-200 dark:bg-zinc-700" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Dots loading animation
  if (type === 'dots') {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        <div className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.3s] dark:bg-zinc-500" />
        <div className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.15s] dark:bg-zinc-500" />
        <div className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 dark:bg-zinc-500" />
      </div>
    );
  }

  // Default spinner
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <Loader2 className={cn('animate-spin text-zinc-400 dark:text-zinc-500', sizeClasses[size])} />
    </div>
  );
}

// Progress bar component for long operations
interface ProgressBarProps {
  /**
   * Progress percentage (0-100)
   */
  value: number;
  /**
   * Show percentage label
   */
  showLabel?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Color variant
   */
  variant?: 'default' | 'success' | 'warning' | 'error';
}

export function ProgressBar({
  value,
  showLabel = false,
  className,
  variant = 'default',
}: ProgressBarProps) {
  const variantClasses = {
    default: 'bg-blue-600 dark:bg-blue-500',
    success: 'bg-green-600 dark:bg-green-500',
    warning: 'bg-amber-600 dark:bg-amber-500',
    error: 'bg-red-600 dark:bg-red-500',
  };

  const clampedValue = Math.min(100, Math.max(0, value));

  return (
    <div className={cn('w-full', className)}>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300 ease-out',
            variantClasses[variant]
          )}
          style={{ width: `${clampedValue}%` }}
        />
      </div>
      {showLabel && (
        <div className="mt-1 text-right text-xs text-zinc-600 dark:text-zinc-400">
          {clampedValue}%
        </div>
      )}
    </div>
  );
}

// Shimmer effect component
interface ShimmerProps {
  /**
   * Additional CSS classes
   */
  className?: string;
}

export function Shimmer({ className }: ShimmerProps) {
  return (
    <div
      className={cn(
        'animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent',
        className
      )}
    />
  );
}
